import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/PageHeader';
import { Building2, Crown, AlertTriangle, CheckCircle, CreditCard, Clock, ArrowRight, Users, Lock, FileText, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_STATUS = {
  TRIAL: { label: 'Trial', color: 'bg-blue-100 text-blue-800', icon: Clock },
  ACTIVE: { label: 'Attivo', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  SUSPENDED: { label: 'Sospeso', color: 'bg-red-100 text-red-800', icon: Lock },
  EXPIRED: { label: 'Scaduto', color: 'bg-red-100 text-red-800', icon: AlertTriangle }
};

export const Workspace = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchWorkspace = useCallback(async () => {
    try {
      const [wsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/workspace`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/workspace/status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (wsRes.ok) setWorkspace(await wsRes.json());
      if (statusRes.ok) setStatus(await statusRes.json());
    } catch (error) {
      console.error('Error fetching workspace:', error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Caricamento...</div>;
  }

  if (!workspace || !status?.has_workspace) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Workspace non configurato</h2>
            <p className="text-muted-foreground">
              Contatta l'amministratore per configurare il tuo workspace.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const planInfo = PLAN_STATUS[status.plan_status] || PLAN_STATUS.TRIAL;
  const PlanIcon = planInfo.icon;
  const trialProgress = status.days_remaining !== null 
    ? Math.max(0, ((10 - status.days_remaining) / 10) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Workspace"
        subtitle="Gestione dell'organizzazione"
      />

      {/* Plan Status Banner */}
      {status.plan_status === 'TRIAL' && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-800">
                    Trial in corso - {status.days_remaining} giorni rimanenti
                  </p>
                  <p className="text-sm text-blue-700">
                    Scade il {formatDate(status.trial_ends_at)}
                  </p>
                </div>
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <CreditCard className="mr-2 h-4 w-4" />
                Attiva Abbonamento
              </Button>
            </div>
            <Progress value={trialProgress} className="mt-3 h-2" />
          </CardContent>
        </Card>
      )}

      {status.plan_status === 'EXPIRED' && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">
                    Trial Scaduta
                  </p>
                  <p className="text-sm text-red-700">
                    Il tuo accesso è limitato alla sola lettura. Attiva un abbonamento per continuare.
                  </p>
                </div>
              </div>
              <Button className="bg-red-600 hover:bg-red-700">
                <CreditCard className="mr-2 h-4 w-4" />
                Attiva Ora
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workspace Info */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Dettagli Workspace
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Nome</span>
              <span className="font-medium">{workspace.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Stato Piano</span>
              <Badge className={planInfo.color}>
                <PlanIcon className="h-3 w-3 mr-1" />
                {planInfo.label}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Creato il</span>
              <span>{formatDate(workspace.created_at)}</span>
            </div>
            {status.subscription_started_at && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Abbonamento dal</span>
                <span>{formatDate(status.subscription_started_at)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Utilizzo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground">Condomini</span>
                <span className="font-medium">
                  {status.condominium_count} / {status.max_condominiums}
                </span>
              </div>
              <Progress 
                value={(status.condominium_count / status.max_condominiums) * 100} 
                className="h-2"
              />
            </div>
            
            {workspace.billing_notes && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">Note:</p>
                <p className="text-sm">{workspace.billing_notes}</p>
              </div>
            )}
            
            {/* Quick Links */}
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">Documenti:</p>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/workspace/terms')}
                  className="text-xs"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Condizioni Contrattuali
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/privacy')}
                  className="text-xs"
                >
                  <Shield className="h-3 w-3 mr-1" />
                  Privacy Policy
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upgrade CTA */}
      {status.plan_status !== 'ACTIVE' && (
        <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-slate-900 rounded-lg">
                  <Crown className="h-8 w-8 text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Passa al Piano PRO</h3>
                  <p className="text-muted-foreground">
                    Sblocca tutte le funzionalità e gestisci più condomini
                  </p>
                </div>
              </div>
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800" onClick={() => navigate('/pricing')}>
                Scopri i Piani
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
